/**
 * PublishProgressModal — Sprint UX Workspace v1.35
 *
 * Premium publish experience.
 *
 * When the user clicks Publish on a Delivery Card we open this modal
 * FIRST, run the real backend publish + share flow in parallel, and
 * show a progressive step-by-step animation. Even if the backend
 * finishes in 5s we keep the animation running for at least 30s so
 * the user perceives each stage.
 *
 * Steps are timed at ~5s each (7 steps × ~5s = ~35s min visible).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, X, Copy, ExternalLink, Share2 } from 'lucide-react';
import { toast } from 'sonner';

export interface PublishedResult {
  url: string;
  thumbnail?: string | null;
  name?: string;
  published_at?: string | null;
}

interface PublishProgressModalProps {
  open: boolean;
  onClose: () => void;
  workspaceName?: string;
  /**
   * The actual backend publish call. Should resolve with the public URL
   * (and optionally thumbnail + timestamp). If it rejects, we still let
   * the animation finish and then show the error card.
   */
  publishFn: () => Promise<PublishedResult>;
}

const STEPS: string[] = [
  'Preparando versión final',
  'Validando archivos',
  'Ejecutando comprobaciones',
  'Generando versión optimizada',
  'Publicando Sandbox',
  'Verificando disponibilidad',
  'Generando URL pública',
];

const STEP_DURATION_MS = 4500;              // per-step visible time
const MIN_TOTAL_MS = 30_000;                // 30s minimum per spec

export const PublishProgressModal: React.FC<PublishProgressModalProps> = ({
  open, onClose, workspaceName, publishFn,
}) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [result, setResult] = useState<PublishedResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const startedAt = useMemo(() => (open ? Date.now() : 0), [open]);
  // Sprint v1.42 · P0 fix — publishFn is defined inline in the parent
  // JSX, so a fresh identity comes with every render. Without this
  // guard the useEffect would re-fire on parent re-renders and issue
  // multiple concurrent POST /publish calls (observed 15+ in a single
  // flow → E11000 dup key storm on published_versions).
  const publishFiredRef = React.useRef(false);

  useEffect(() => {
    if (!open) { publishFiredRef.current = false; return; }
    if (publishFiredRef.current) return; // already firing for this open cycle
    publishFiredRef.current = true;
    setStepIdx(0); setResult(null); setError(null); setDone(false);

    // 1) Fire the real publish call in parallel.
    let realResult: PublishedResult | null = null;
    let realError: string | null = null;
    const publishPromise = publishFn().then(
      (r) => { realResult = r; },
      (e) => { realError = e?.message || 'La publicación no pudo completarse'; },
    );

    // 2) Walk through the visual steps monotonically.
    // Sprint v1.38 · The user reported the checklist "advances then
    // goes back". Guarantee monotonic progression: setStepIdx only
    // ever moves forward. Each timer bumps if and only if the new
    // index is strictly greater than the current one.
    const timers: number[] = [];
    STEPS.forEach((_s, i) => {
      const t = window.setTimeout(
        () => setStepIdx((cur) => (i > cur ? i : cur)),
        i * STEP_DURATION_MS,
      );
      timers.push(t);
    });

    // 3) When BOTH the real publish AND the min-30s clock complete, reveal.
    const minTimer = window.setTimeout(() => {}, MIN_TOTAL_MS);
    timers.push(minTimer);
    (async () => {
      await Promise.all([
        publishPromise,
        new Promise<void>((resolve) => window.setTimeout(resolve, MIN_TOTAL_MS)),
      ]);
      // Monotonic reveal: never decrement below the terminal state.
      setStepIdx((cur) => Math.max(cur, STEPS.length));
      if (realError) setError(realError);
      else if (realResult) setResult(realResult);
      setDone(true);
    })();

    return () => { timers.forEach(clearTimeout); };
  }, [open, startedAt, publishFn]);

  if (!open) return null;

  const elapsed = Math.floor((Date.now() - startedAt) / 1000);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6"
      style={{ background: 'rgba(2, 6, 23, 0.88)', backdropFilter: 'blur(10px)' }}
      data-testid="publish-progress-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.22 }}
        className="w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl"
        style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))' }}
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest opacity-70" style={{ color: 'hsl(var(--accent))' }}>Publicación</div>
            <div className="text-sm font-semibold truncate mt-0.5" style={{ color: 'hsl(var(--text))' }}>
              {workspaceName || 'Tu aplicación'}
            </div>
          </div>
          {done && (
            <button onClick={onClose} className="w-8 h-8 rounded-full grid place-items-center transition-colors"
              style={{ background: 'hsl(var(--bg-elev))', color: 'hsl(var(--text-muted))' }}
              aria-label="Cerrar" data-testid="publish-progress-close">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Body */}
        {!done ? (
          <div className="px-5 py-6 space-y-3">
            <div className="text-[11px] uppercase tracking-widest opacity-60" style={{ color: 'hsl(var(--text-muted))' }}>
              Estamos publicando · {elapsed}s
            </div>
            {STEPS.map((s, i) => {
              const state: 'done' | 'active' | 'pending' =
                i < stepIdx ? 'done' : i === stepIdx ? 'active' : 'pending';
              return (
                <div
                  key={s}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors"
                  style={{
                    background: state === 'active' ? 'hsl(var(--accent) / 0.08)' : 'hsl(var(--bg-elev) / 0.6)',
                    border: `1px solid ${state === 'active' ? 'hsl(var(--accent) / 0.4)' : 'hsl(var(--border))'}`,
                  }}
                  data-testid={`publish-step-${i}`}
                >
                  <span
                    className="w-6 h-6 rounded-full grid place-items-center shrink-0"
                    style={{
                      background: state === 'done' ? 'hsl(var(--accent))' : 'hsl(var(--bg))',
                      color: state === 'done' ? 'hsl(210 40% 8%)' : 'hsl(var(--accent))',
                      border: state === 'done' ? 'none' : `1px solid ${state === 'active' ? 'hsl(var(--accent))' : 'hsl(var(--border))'}`,
                    }}
                  >
                    {state === 'done' ? <Check size={12} />
                      : state === 'active' ? <Loader2 size={12} className="animate-spin" />
                      : <span className="text-[9px] opacity-70">{i + 1}</span>}
                  </span>
                  <span
                    className="text-xs flex-1"
                    style={{ color: state === 'pending' ? 'hsl(var(--text-muted))' : 'hsl(var(--text))' }}
                  >
                    {s}
                  </span>
                </div>
              );
            })}
          </div>
        ) : error ? (
          <PublishErrorCard message={error} onClose={onClose} />
        ) : result ? (
          <PublishSuccessCard result={result} name={workspaceName} onClose={onClose} />
        ) : null}
      </motion.div>
    </div>
  );
};

const PublishErrorCard: React.FC<{ message: string; onClose: () => void }> = ({ message, onClose }) => (
  <div className="px-5 py-6 space-y-4" data-testid="publish-error-card">
    <div className="text-sm font-medium" style={{ color: 'hsl(0 84% 65%)' }}>
      No se pudo publicar
    </div>
    <div className="text-xs" style={{ color: 'hsl(var(--text-muted))' }}>{message}</div>
    <button
      onClick={onClose}
      className="px-4 py-2 rounded-full text-xs font-medium"
      style={{ background: 'hsl(var(--bg-elev))', color: 'hsl(var(--text))', border: '1px solid hsl(var(--border))' }}
      data-testid="publish-error-close"
    >Entendido</button>
  </div>
);

const PublishSuccessCard: React.FC<{ result: PublishedResult; name?: string; onClose: () => void }> = ({ result, name, onClose }) => {
  const dateStr = useMemo(() => {
    const d = result.published_at ? new Date(result.published_at) : new Date();
    return d.toLocaleString();
  }, [result.published_at]);
  const copy = async () => { await navigator.clipboard.writeText(result.url); toast.success('URL copiada'); };
  const share = async () => {
    try {
      if ((navigator as any).share) await (navigator as any).share({ title: name || 'Aplicación', url: result.url });
      else await copy();
    } catch { /* user cancelled */ }
  };
  return (
    <AnimatePresence>
      <motion.div
        key="publish-success"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="px-5 py-6 space-y-4"
        data-testid="publish-success-card"
      >
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid hsl(var(--accent) / 0.35)', background: 'hsl(var(--bg-elev))' }}>
          {result.thumbnail && (
            <img
              src={result.thumbnail}
              alt="thumbnail"
              className="w-full object-cover"
              style={{ height: 180 }}
              data-testid="publish-success-thumbnail"
            />
          )}
          <div className="px-4 py-3 space-y-1">
            <div className="text-sm font-semibold" style={{ color: 'hsl(var(--text))' }}>{name || 'Aplicación publicada'}</div>
            <div className="flex items-center gap-1.5 text-[11px]" style={{ color: 'hsl(140 65% 60%)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
              En línea
            </div>
            <div className="text-[11px] truncate" style={{ color: 'hsl(var(--text-muted))' }}>{result.url}</div>
            <div className="text-[10px] opacity-70 mt-0.5" style={{ color: 'hsl(var(--text-muted))' }}>Publicada el {dateStr}</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a href={result.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors"
            style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
            data-testid="publish-success-open">
            <ExternalLink size={12} /> Abrir
          </a>
          <button onClick={copy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-colors"
            style={{ background: 'hsl(var(--bg-elev))', color: 'hsl(var(--text))', border: '1px solid hsl(var(--border))' }}
            data-testid="publish-success-copy">
            <Copy size={12} /> Copiar
          </button>
          <button onClick={share}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-colors"
            style={{ background: 'hsl(var(--bg-elev))', color: 'hsl(var(--text))', border: '1px solid hsl(var(--border))' }}
            data-testid="publish-success-share">
            <Share2 size={12} /> Compartir
          </button>
          <button onClick={onClose}
            className="ml-auto text-[11px] opacity-70 hover:opacity-100"
            style={{ color: 'hsl(var(--text-muted))' }}
            data-testid="publish-success-close">Cerrar</button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PublishProgressModal;
